// WAAP Advanced Protection — uploaded static files (honeypot HTML pages, XML
// schemas/WSDLs, …). Backed by the PRIVATE portal /static_file(s) via
// eagle_waf/advanced. Account-level: the `:id` domain only resolves the customer
// session, so the hooks take an ANCHOR domain UUID. Files are scoped by `type`
// (e.g. "honeypot_protection").
//
//   GET  /waap/domains/:id/static-files?type=X    → portal GET  /static_files?type=X
//   POST /waap/domains/:id/static-files/upload    → portal POST /static_file/upload

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { serviceApiV3 } from '@/api/client';

/** An uploaded static file. */
export interface StaticFile {
  id: number;
  url: string;
  name: string;
  type: string;
  created?: string;
  updated?: string;
  status?: number;
  customer_id?: number;
}

/** A file-upload request (the file's text content + metadata). */
export interface StaticFileUpload {
  content: string;
  name: string;
  type: string;
  /** Usually empty. */
  path?: string;
  /** Existing file id to replace in place; 0 (or omit) to create a new file.
   *  The vendor replaces by re-POSTing with the same id, not delete-then-upload. */
  id?: number;
}

function basePath(domainId: string): string {
  return `/waap/domains/${domainId}/static-files`;
}

// Service-scoped twin: static files are account-level (scoped to the vendor
// customer), so they resolve from the SERVICE — no carrier domain needed. This
// path works with zero websites; the domain-scoped path above stays for callers
// not yet migrated.
function serviceBasePath(serviceId: string): string {
  return `/services/${serviceId}/waap/static-files`;
}

export const staticFileKeys = {
  all: ['waap', 'static-files'] as const,
  list: (domainId: string, type: string) => ['waap', 'static-files', domainId, type] as const,
  serviceList: (serviceId: string, type: string) =>
    ['waap', 'static-files', 'service', serviceId, type] as const,
};

/** List the account's uploaded static files. An empty `type` lists every file. */
export function useStaticFiles(domainId: string | undefined, type = '') {
  return useQuery({
    queryKey: staticFileKeys.list(domainId ?? '', type),
    enabled: !!domainId,
    retry: false,
    queryFn: async (): Promise<StaticFile[]> => {
      const res = await serviceApiV3.get<StaticFile[] | { data: StaticFile[] }>(
        basePath(domainId as string),
        type ? { params: { type } } : undefined,
      );
      const body = res.data as StaticFile[] | { data?: StaticFile[] };
      return Array.isArray(body) ? body : body?.data ?? [];
    },
  });
}

/** Download a static file's raw content as a browser download. */
export async function downloadStaticFile(domainId: string, file: StaticFile): Promise<void> {
  const res = await serviceApiV3.get<string>(`${basePath(domainId)}/${file.id}/content`, {
    responseType: 'text',
  });
  const content = typeof res.data === 'string' ? res.data : String(res.data);
  const url = URL.createObjectURL(new Blob([content], { type: 'application/octet-stream' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = file.name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Delete an uploaded static file by id. (The BE clears it via the vendor's
 *  POST /static_file/clear under the hood.) */
export function useDeleteStaticFile(
  domainId: string,
  _type = '',
  options?: UseMutationOptions<unknown, unknown, number>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await serviceApiV3.delete(`${basePath(domainId)}/${id}`);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      // Refresh every static-file list (the page lists with an empty `type`).
      qc.invalidateQueries({ queryKey: staticFileKeys.all });
      options?.onSuccess?.(...args);
    },
  });
}

/** Upload a static file, then refresh the list for its type. */
export function useUploadStaticFile(
  domainId: string,
  options?: UseMutationOptions<unknown, unknown, StaticFileUpload>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: StaticFileUpload) => {
      const res = await serviceApiV3.post(`${basePath(domainId)}/upload`, {
        path: '',
        id: 0,
        ...body,
      });
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      // Refresh every static-file list (the page lists with an empty `type`).
      qc.invalidateQueries({ queryKey: staticFileKeys.all });
      options?.onSuccess?.(...args);
    },
  });
}

// ---- Service-scoped twins (account-level, work with zero websites) ----------

/** List the account's static files, resolved from the service (no domain anchor). */
export function useServiceStaticFiles(serviceId: string | undefined, type = '') {
  return useQuery({
    queryKey: staticFileKeys.serviceList(serviceId ?? '', type),
    enabled: !!serviceId,
    retry: false,
    queryFn: async (): Promise<StaticFile[]> => {
      const res = await serviceApiV3.get<StaticFile[] | { data: StaticFile[] }>(
        serviceBasePath(serviceId as string),
        type ? { params: { type } } : undefined,
      );
      const body = res.data as StaticFile[] | { data?: StaticFile[] };
      return Array.isArray(body) ? body : body?.data ?? [];
    },
  });
}

/** Download a static file's raw content, service-scoped. */
export async function downloadServiceStaticFile(serviceId: string, file: StaticFile): Promise<void> {
  const res = await serviceApiV3.get<string>(`${serviceBasePath(serviceId)}/${file.id}/content`, {
    responseType: 'text',
  });
  const content = typeof res.data === 'string' ? res.data : String(res.data);
  const url = URL.createObjectURL(new Blob([content], { type: 'application/octet-stream' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = file.name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Delete a static file by id, service-scoped. */
export function useDeleteServiceStaticFile(
  serviceId: string,
  options?: UseMutationOptions<unknown, unknown, number>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await serviceApiV3.delete(`${serviceBasePath(serviceId)}/${id}`);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: staticFileKeys.all });
      options?.onSuccess?.(...args);
    },
  });
}

/** Upload a static file, service-scoped, then refresh the lists. */
export function useUploadServiceStaticFile(
  serviceId: string,
  options?: UseMutationOptions<unknown, unknown, StaticFileUpload>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: StaticFileUpload) => {
      const res = await serviceApiV3.post(`${serviceBasePath(serviceId)}/upload`, {
        path: '',
        id: 0,
        ...body,
      });
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: staticFileKeys.all });
      options?.onSuccess?.(...args);
    },
  });
}
